import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable react/require-default-props */
import DynamicFilterDropdown from '@/shared/components/DynamicFilterDropdown';
import React, { useMemo, useState } from 'react';
import { FaUndo } from 'react-icons/fa';
import { defineMessages, useIntl } from 'react-intl';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import userStorage from '../services/userStorage';
import { FilterSave } from './FilterSave';
import { WeekPlannerFilterType } from '@/modules/tasks/weekplanner/constants';
const onlyUnique = (value, index, self) => self.indexOf(value) === index;
const selectAll = 'selectAll';
const messages = defineMessages({
    ResetFilters: {
        id: 'ResetFilters',
        defaultMessage: 'Reset filters',
    },
    SelectAll: {
        id: 'SelectAll',
        defaultMessage: 'Select all',
    },
});
export const FilterWrapper = ({ filterObject, children, bottomElement, screen, setFilter, hasActiveFilters, resetFilters, invisibleFilters, // meant to be used when a screen has a filter that cannot be manually selected, like a route parameter for a single task on the taskboard
 }) => {
    const { formatMessage } = useIntl();
    const filterSelectItems = useMemo(() => children.filter(item => item.filterSelectLabel && item.enabled).map(item => item.filterItem), [children]);
    const [selectedFilterItems, _setSelectedFilterItems] = useState(userStorage.getItem(`${screen}-Filters`) || []);
    const setSelectedFilterItems = items => _setSelectedFilterItems(prev => {
        const newItems = typeof items === 'function' ? items(prev) : items;
        userStorage.setItem(`${screen}-Filters`, newItems);
        return newItems;
    });
    const filterSelectList = useMemo(() => [
        {
            id: selectAll,
            label: formatMessage(messages.SelectAll),
            selected: selectedFilterItems.includes(selectAll),
        },
        ...filterSelectItems
            .map((key) => ({
            id: key,
            label: children.find(item => item.filterItem === key).filterSelectLabel,
            selected: selectedFilterItems.includes(key),
        }))
            .sort(dynamicSort('label')),
    ], [children, filterSelectItems, formatMessage, selectedFilterItems]);
    const onChangeFilterSelect = (selectedItem, selected, visibleItemIds) => {
        if (selectedItem.id === selectAll) {
            if (visibleItemIds) {
                // Search-filtered select all: only toggle visible items
                if (selected) {
                    setSelectedFilterItems(prev => {
                        const newItems = [...prev, ...visibleItemIds.filter(id => !prev.includes(id))];
                        if (newItems.filter(id => id !== selectAll).length >= filterSelectItems.length) {
                            return [selectAll, ...filterSelectItems];
                        }
                        return newItems;
                    });
                }
                else {
                    setSelectedFilterItems(prev => prev.filter(item => (item === selectAll ? false : !visibleItemIds.includes(item))));
                }
            }
            else if (selected) {
                setSelectedFilterItems([selectAll, ...filterSelectItems]);
            }
            else {
                setSelectedFilterItems([]);
            }
        }
        else {
            // eslint-disable-next-line no-lonely-if
            if (selected) {
                if (selectedFilterItems.length + 1 >= filterSelectItems.length) {
                    setSelectedFilterItems([selectAll, ...filterSelectItems]);
                }
                else {
                    setSelectedFilterItems(prev => [...prev, selectedItem.id]);
                }
            }
            else {
                setSelectedFilterItems(prev => prev.filter(item => item !== selectedItem.id && item !== selectAll));
            }
        }
    };
    const includedFilterKeys = useMemo(() => [
        ...children.map(item => item.filterItem),
        ...children.reduce((prev, item) => { var _a; return [...prev, ...((_a = item.extraFilterItems) !== null && _a !== void 0 ? _a : [])]; }, []),
        ...invisibleFilters,
    ].filter(onlyUnique), [children, invisibleFilters]);
    const includedFiltersObject = useMemo(() => Object.keys(filterObject).reduce((prev, key) => {
        if ((!filterObject[key] || !includedFilterKeys.includes(key)) && filterObject[key] !== 0) {
            return prev;
        }
        return Object.assign(Object.assign({}, prev), { [key]: filterObject[key] });
    }, {}), [filterObject, includedFilterKeys]);
    const internalHasActiveFilters = useMemo(() => !!hasActiveFilters
        ? hasActiveFilters()
        : Object.keys(includedFiltersObject)
            .filter(item => item !== WeekPlannerFilterType.executionDateFrom &&
            item !== WeekPlannerFilterType.executionDateUntil)
            .map(item => includedFiltersObject[item])
            .filter(item => (Array.isArray(item) ? item.length > 0 : item === 0 || Boolean(item)))
            .length > 0, [includedFiltersObject]);
    const internalResetFilters = resetFilters ||
        (() => {
            setFilter(includedFilterKeys.reduce((prev, item) => (Object.assign(Object.assign({}, prev), { [item]: item === WeekPlannerFilterType.executionDateFrom ||
                    item === WeekPlannerFilterType.executionDateUntil
                    ? filterObject[item]
                    : undefined })), {}));
        });
    return (_jsxs(React.Fragment, { children: [_jsx("div", Object.assign({ className: "row" }, { children: children
                    .filter(x => x.filterItem)
                    .map(child => {
                    var _a;
                    if (!child.element || !child.enabled) {
                        return null;
                    }
                    if (child.filterSelectLabel &&
                        !selectedFilterItems.includes(child.filterItem)) {
                        return null;
                    }
                    return (_jsx("div", Object.assign({ className: (_a = child.className) !== null && _a !== void 0 ? _a : 'col-lg-3 col-md-6' }, { children: _jsx("div", Object.assign({ className: "form-group width100pct" }, { children: child.element })) }), child.filterItem));
                })
                    .filter(Boolean) })), _jsxs("div", Object.assign({ className: "flex-row" }, { children: [_jsxs("div", Object.assign({ className: "btn-group save-filters" }, { children: [_jsx(FilterSave, { hasActiveFilters: internalHasActiveFilters, screen: screen, filtersObject: includedFiltersObject, resetFilters: internalResetFilters, setFilter: setFilter }), filterSelectItems.length > 0 && (_jsx(DynamicFilterDropdown, { items: filterSelectList, onChange: onChangeFilterSelect })), internalHasActiveFilters && (_jsxs("button", Object.assign({ type: "button", className: "btn btn-default btn-reset", onClick: internalResetFilters }, { children: [_jsx(FaUndo, { className: "react-icon fa-fw margin5R" }), formatMessage(messages.ResetFilters)] })))] })), bottomElement] }))] }));
};
